import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import MarkerIcon from '../../atoms/MarkerIcon';
import ThreeDotsButton from '../../atoms/ThreeDotsButton';
import DropdownMenuFloat from '../DropdownMenuFloat';
import './DiscoverFilterToggle.scss';

function DiscoverFilterActions({ actions }) {
    const btnRef = useRef(null);
    const [menuPos, setMenuPos] = useState(null);

    const openMenu = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = btnRef.current?.getBoundingClientRect();
        if (!rect) return;
        setMenuPos({ x: rect.right, y: rect.bottom + 4 });
    }, []);

    const closeMenu = useCallback(() => setMenuPos(null), []);

    return (
        <span className="discover-filter__actions">
            <ThreeDotsButton
                ref={btnRef}
                onClick={openMenu}
                className="discover-filter__dots-btn"
                size={18}
            />
            {menuPos && (
                <DropdownMenuFloat
                    position={menuPos}
                    items={actions}
                    onClose={closeMenu}
                />
            )}
        </span>
    );
}

export default function DiscoverFilterToggle({
    label,
    countLabel = '',
    checked = true,
    onChange,
    tone = 'event',
    name,
    color = '#5f8cff',
    onColorChange = undefined,
    disabled = false,
    disabledReason = '',
    actions = undefined,
}) {
    const { t } = useTranslation();
    const isDisabled = disabled === true;
    const handleColorClick = () => {
        if (isDisabled) {
            return;
        }
        const input = document.getElementById(`${name}-color-picker`);
        if (input) {
            input.showPicker?.();
            input.click();
        }
    };

    const handleColorInput = (event) => {
        if (isDisabled) {
            return;
        }
        onColorChange?.(event.target.value);
    };

    return (
        <div
            className={`discover-filter discover-filter--${tone}${isDisabled ? ' discover-filter--disabled' : ''}`}
            aria-disabled={isDisabled}
            title={isDisabled && disabledReason ? disabledReason : undefined}
        >
            <button
                type="button"
                className="discover-filter__swatch"
                aria-label={label}
                onClick={handleColorClick}
                disabled={isDisabled}
            >
                <MarkerIcon color={color} size="small" ariaLabel={label} />
            </button>
            <div className="discover-filter__labels">
                <span className="discover-filter__title">{label}</span>
                {countLabel && checked && (
                    <span className="discover-filter__subtitle discover-filter__subtitle--animated">
                        {countLabel}
                    </span>
                )}
            </div>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <label
                className="discover-filter__switch"
                htmlFor={name}
                onPointerDown={(e) => e.stopPropagation()}
            >
                <input
                    id={name}
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => onChange(event.target.checked)}
                    disabled={isDisabled}
                />
                <span className="discover-filter__track" aria-hidden />
                <span className="discover-filter__thumb" aria-hidden />
            </label>
            {Array.isArray(actions) && actions.length > 0 && (
                <DiscoverFilterActions actions={actions} />
            )}
            <input
                id={`${name}-color-picker`}
                type="color"
                className="discover-filter__color-picker"
                value={color}
                onChange={handleColorInput}
                aria-label={t('discoverPage.panel.colorPickerAria', 'Choose marker color')}
                disabled={isDisabled}
            />
        </div>
    );
}

DiscoverFilterToggle.propTypes = {
    label: PropTypes.string.isRequired,
    countLabel: PropTypes.string,
    checked: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    tone: PropTypes.oneOf(['event', 'session', 'venue', 'artist', 'band']),
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    onColorChange: PropTypes.func,
    disabled: PropTypes.bool,
    disabledReason: PropTypes.string,
    actions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.node,
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        ariaLabel: PropTypes.string,
        tone: PropTypes.string,
    })),
};
